import { useQueryClient } from '@tanstack/react-query';
import { MOCK_PAYMENT_SETTINGS } from '@/lib/demo-data/payment-settings';
import { updatePaymentSettingsDemo } from '@/services/demo/payment-settings-demo.service';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';

const SETTINGS_PATH = '/api/v1/settings/payments';
const SETTINGS_KEY = ['payment-settings'];

export interface PaymentSettings {
  /** Set once the organisation has completed PaySuite merchant onboarding. */
  onboardingCompleted: boolean;
  onboardingCompletedAtUtc: string | null;
  onboardingUpdatedAtUtc: string | null;
  /** Set once onboarding has completed and PaySuite has reported the merchant a link can be raised for. */
  paymentLinksAvailable: boolean;
  bannerDismissed: boolean;
  bannerUpdatedAtUtc: string | null;
}

/** Merge-patch update: an omitted field keeps its stored value, so callers only send what they own. */
export interface UpdatePaymentSettingsRequest {
  onboardingCompleted?: boolean;
  bannerDismissed?: boolean;
}

/**
 * Payment settings of the current organisation. The endpoint is admin-only, so callers that render
 * for every role must pass `enabled` to keep the request out of a non-admin session; `forceLive`
 * bypasses the screen mode when tenant state must be read. Onboarding completion is set out-of-band
 * by the PaySuite webhook, so this shared query uses a short stale time to avoid holding a stale
 * incomplete value for long.
 */
export function usePaymentSettings(options?: { enabled?: boolean; forceLive?: boolean }) {
  const { apiGet } = useApi();

  return useModeAwareQuery<PaymentSettings, Error>(
    SETTINGS_KEY,
    async ({ signal }) => apiGet<PaymentSettings>(SETTINGS_PATH, undefined, signal),
    MOCK_PAYMENT_SETTINGS,
    {
      staleTime: options?.forceLive ? 0 : 30 * 1000,
      enabled: options?.enabled,
      forceLive: options?.forceLive,
    },
  );
}

/**
 * Writes the fields supplied to the tenant-wide payment settings; anything omitted keeps its stored
 * value. Dismissing the banner hides it for every admin of the organisation.
 */
export function useUpdatePaymentSettings() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<PaymentSettings, Error, UpdatePaymentSettingsRequest>(
    async (data) => apiPut<PaymentSettings>(SETTINGS_PATH, data),
    updatePaymentSettingsDemo,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
      },
    },
  );
}
